import { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { Search, Loader2, Edit, Trash2, RefreshCw, Tag, Star, Eye } from 'lucide-react';
import { Trader } from '../../services/traderService';
import { toast } from '../../components/ui/use-toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { triggerKeywordGeneration } from '../../services/keywordService';
import EditTraderForm from '../../components/admin/EditTraderForm';
import AdminFeatureManager from '../../components/admin/AdminFeatureManager';
import AdminBusinessPreviewModal from '../../components/admin/AdminBusinessPreviewModal';

interface EditingTrader extends Trader {
  isEditing?: boolean;
  professions?: { name: string };
  business_types?: { id: string; name: string };
  keywords?: string[];
}

const Traders = () => {
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [traders, setTraders] = useState<EditingTrader[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrader, setSelectedTrader] = useState<Trader | null>(null);
  const [regeneratingKeywords, setRegeneratingKeywords] = useState<Set<number>>(new Set());
  const [featureManagerTrader, setFeatureManagerTrader] = useState<EditingTrader | null>(null); // State for feature manager modal
  const [previewTrader, setPreviewTrader] = useState<EditingTrader | null>(null);

  useEffect(() => {
    const loadTraders = async () => {
      if (authLoading) return;
      
      if (!adminUser) {
        setError('Not authorized');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Load traders first, then get business types separately
        const { data: tradersData, error: tradersError } = await supabaseAdmin
          .from('traders')
          .select('*')
          .order('store_name', { ascending: true });

        if (tradersError) {
          console.error('Error loading traders:', tradersError);
          throw tradersError;
        }

        // Get all business types
        const { data: businessTypes, error: businessTypesError } = await supabaseAdmin
          .from('business_types')
          .select('id, name');

        if (businessTypesError) {
          console.error('Error loading business types:', businessTypesError);
          throw businessTypesError;
        }

        // Now fetch profession names based on business type
        const tradersWithProfessions = await Promise.all(
          (tradersData || []).map(async (trader) => {
            // Find the business type for this trader
            const businessType = businessTypes?.find(bt => bt.id === trader.business_type_id);
            const businessTypeName = businessType?.name?.toLowerCase();
            let professionName = 'Unknown';

            try {
              if (businessTypeName === 'tradesman') {
                const { data: profession } = await supabaseAdmin
                  .from('professions')
                  .select('name')
                  .eq('id', trader.profession_id)
                  .single();
                professionName = profession?.name || 'Unknown';
              } else if (businessTypeName === 'craftsman') {
                const { data: profession } = await supabaseAdmin
                  .from('craft_professions')
                  .select('name')
                  .eq('id', trader.profession_id)
                  .single();
                professionName = profession?.name || 'Unknown';
              } else if (businessTypeName === 'hospitality') {
                const { data: profession } = await supabaseAdmin
                  .from('hospitality_categories')
                  .select('name')
                  .eq('id', trader.profession_id)
                  .single();
                professionName = profession?.name || 'Unknown';
              } else if (businessTypeName && businessTypeName.includes('professional')) {
                // Professional Services traders still use the professions table for their profession_id
                const { data: profession } = await supabaseAdmin
                  .from('professions')
                  .select('name')
                  .eq('id', trader.profession_id)
                  .single();
                professionName = profession?.name || 'Unknown';
              }
            } catch (err) {
              console.error(`Error fetching profession for trader ${trader.id}:`, err);
            }

            return {
              ...trader,
              professions: { name: professionName },
              business_types: businessType
            };
          })
        );

        console.log('Loaded traders with professions:', tradersWithProfessions);
        setTraders(tradersWithProfessions || []);
      } catch (err) {
        console.error('Error loading traders:', err);
        setError(err instanceof Error ? err.message : 'Failed to load traders');
        toast({
          title: 'Error',
          description: 'Failed to load traders data',
          variant: 'destructive',
        });
        setTraders([]);
      } finally {
        setLoading(false);
      }
    };

    loadTraders();
  }, [adminUser, authLoading]);

  const handleVerifyTrader = async (trader: Trader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('traders')
        .update({ verified: !trader.verified })
        .eq('id', trader.id);

      if (updateError) throw updateError;

      setTraders(prev => 
        prev.map(t => 
          t.id === trader.id 
            ? { ...t, verified: !t.verified }
            : t
        )
      );

      toast({
        title: 'Success',
        description: `Trader ${trader.verified ? 'unverified' : 'verified'} successfully`,
      });
    } catch (error) {
      console.error('Error updating trader:', error);
      toast({
        title: 'Error',
        description: 'Failed to update trader status',
        variant: 'destructive',
      });
    }
  };

  const handleToggleMapVisibility = async (trader: EditingTrader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    const current = trader.show_map ?? true;
    const next = !current;

    try {
      const { error: updateError } = await supabase
        .from('traders')
        .update({ show_map: next })
        .eq('id', trader.id);

      if (updateError) {
        throw updateError;
      }

      setTraders(prev =>
        prev.map(t =>
          t.id === trader.id
            ? { ...t, show_map: next }
            : t
        )
      );

      toast({
        title: 'Success',
        description: `Map ${next ? 'enabled' : 'disabled'} for this trader`,
      });
    } catch (error) {
      console.error('Error updating map visibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to update map visibility',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTrader = async (trader: Trader) => {
    if (!adminUser) {
      toast({
        title: 'Error',
        description: 'Not authorized to perform this action',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${trader.store_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Starting deletion process for trader:', trader.id);
      
      // First check if the trader still exists and get their auth user ID
      const { data: existingTrader, error: checkError } = await supabaseAdmin
        .from('traders')
        .select('id, user_id, email')
        .eq('id', trader.id)
        .single();

      if (checkError) {
        console.error('Error checking trader existence:', checkError);
        throw new Error('Failed to verify trader record');
      }

      if (!existingTrader) {
        console.log('Trader already deleted:', trader.id);
        setTraders(prev => prev.filter(t => t.id !== trader.id));
        toast({
          title: 'Info',
          description: 'Trader record no longer exists',
        });
        return;
      }

      console.log('Found trader:', existingTrader);

      // Delete the trader record first
      console.log('Deleting trader record:', trader.id);
      const { error: deleteTraderError } = await supabaseAdmin
        .from('traders')
        .delete()
        .eq('id', trader.id);

      if (deleteTraderError) {
        console.error('Error deleting trader:', deleteTraderError);
        throw new Error('Failed to delete trader record');
      }

      console.log('Trader deleted successfully');

      // Now try to clean up auth user if it exists
      if (existingTrader.user_id) {
        console.log('Attempting to delete auth user:', existingTrader.user_id);
        
        try {
          // First disable the user
          const { error: disableError } = await supabaseAdmin.auth.admin.updateUserById(
            existingTrader.user_id,
            { ban_duration: '1000000h' }
          );

          if (disableError) {
            console.error('Error disabling user:', disableError);
            toast({
              title: 'Warning',
              description: 'Failed to disable user account, but trader was deleted',
              variant: 'destructive',
            });
          } else {
            console.log('User disabled successfully');
          }

          // Then try to delete
          const { error: deleteAuthError } = await supabaseAdmin.auth.admin.deleteUser(
            existingTrader.user_id
          );

          if (deleteAuthError) {
            console.error('Error deleting auth user:', deleteAuthError);
            toast({
              title: 'Warning',
              description: 'Failed to delete user account, but trader was deleted. Account has been disabled.',
              variant: 'destructive',
            });
          } else {
            console.log('Auth user deleted successfully');
          }
        } catch (authError) {
          console.error('Exception in auth operations:', authError);
          toast({
            title: 'Warning',
            description: 'Error managing user account, but trader was deleted',
            variant: 'destructive',
          });
        }
      } else {
        console.log('No user_id found for trader');
      }
      
      // Update local state
      setTraders(prev => prev.filter(t => t.id !== trader.id));

      toast({
        title: 'Success',
        description: 'Trader deleted successfully',
      });
    } catch (error) {
      console.error('Error in deletion process:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete trader',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (trader: Trader) => {
    setSelectedTrader(trader);
  };

  const handleSave = (updatedTrader: Trader) => {
    setTraders(prevTraders =>
      prevTraders.map(t =>
        t.id === updatedTrader.id ? { ...updatedTrader } : t
      )
    );
    setSelectedTrader(null);
    toast({
      title: 'Success',
      description: 'Trader information updated successfully',
    });
  };

  const handleClose = () => {
    setSelectedTrader(null);
  };

  const handleRegenerateKeywords = async (trader: EditingTrader) => {
    const traderId = Number(trader.id);
    if (isNaN(traderId)) return;
    
    setRegeneratingKeywords(prev => new Set(prev).add(traderId));
    
    try {
      const success = await triggerKeywordGeneration(
        traderId,
        'trader',
        {
          store_name: trader.store_name,
          description: trader.description,
          location: trader.location
        }
      );
      
      if (success) {
        toast({
          title: 'Success',
          description: `Keyword generation started for ${trader.store_name}`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to trigger keyword generation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error triggering keyword generation:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger keyword generation',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(traderId);
        return newSet;
      });
    }
  };

  const filteredTraders = traders.filter(trader => {
    const searchStr = searchQuery.toLowerCase();
    
    // Check if any keyword matches the search query
    const keywordMatch = trader.keywords?.some(keyword => 
      keyword.toLowerCase().includes(searchStr)
    ) || false;
    
    return (
      trader.store_name?.toLowerCase().includes(searchStr) ||
      trader.owner_name?.toLowerCase().includes(searchStr) ||
      trader.email?.toLowerCase().includes(searchStr) ||
      trader.location?.toLowerCase().includes(searchStr) ||
      trader.professions?.name?.toLowerCase().includes(searchStr) ||
      keywordMatch
    );
  });

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Not authorized to view this page</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Traders</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search traders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTraders.map((trader) => (
            <div
              key={trader.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <img
                    src={trader.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${trader.store_name}`}
                    alt={trader.store_name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{trader.store_name}</h3>
                    <p className="text-sm text-gray-600">{trader.owner_name}</p>
                    <p className="text-sm text-gray-500">
                      {trader.professions?.name || 'No profession'}
                      {trader.business_types?.name && ` (${trader.business_types.name})`}
                    </p>
                    <p className="text-sm text-gray-500">{trader.location}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                  <span>Map visibility</span>
                  <button
                    type="button"
                    onClick={() => handleToggleMapVisibility(trader)}
                    className={`px-2 py-1 rounded-full border text-xs font-medium ${
                      (trader.show_map ?? true)
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  >
                    {(trader.show_map ?? true) ? 'On' : 'Off'}
                  </button>
                </div>

                {/* Keywords Display */}
                {trader.keywords && trader.keywords.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {trader.keywords.slice(0, 5).map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                        {trader.keywords.length > 5 && (
                          <span className="inline-block px-2 py-0.5 text-xs text-gray-500">
                            +{trader.keywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      trader.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {trader.verified ? 'Verified' : 'Pending'}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewTrader(trader)}
                        className="p-2 rounded-full bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors duration-200"
                        title="Quick Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFeatureManagerTrader(trader)}
                        disabled={!trader.user_id}
                        className="p-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={trader.user_id ? 'Manage Features' : 'No user_id - cannot manage features'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRegenerateKeywords(trader)}
                        disabled={regeneratingKeywords.has(Number(trader.id))}
                        className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate Keywords"
                      >
                        <RefreshCw className={`w-4 h-4 ${regeneratingKeywords.has(Number(trader.id)) ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleVerifyTrader(trader)}
                        className={`p-2 rounded-full transition-colors duration-200 ${
                          trader.verified
                            ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                        title={trader.verified ? 'Unverify Trader' : 'Verify Trader'}
                      >
                        {trader.verified ? '✓' : '○'}
                      </button>
                      <button
                        onClick={() => handleEdit(trader)}
                        className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors duration-200"
                        title="Edit Trader"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTrader(trader)}
                        className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors duration-200"
                        title="Delete Trader"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {selectedTrader && (
        <EditTraderForm
          trader={selectedTrader as any}
          onClose={handleClose}
          onSave={handleSave as any}
        />
      )}

      {/* Feature Manager Modal */}
      {featureManagerTrader && featureManagerTrader.user_id && (
        <AdminFeatureManager
          userId={featureManagerTrader.user_id}
          businessName={featureManagerTrader.store_name}
          onClose={() => setFeatureManagerTrader(null)}
          onSave={() => {
            toast({
              title: 'Success',
              description: `Features updated for ${featureManagerTrader.store_name}`,
            });
          }}
        />
      )}

      {previewTrader && (
        <AdminBusinessPreviewModal
          businessType="trader"
          business={previewTrader}
          contextLabel={previewTrader.professions?.name}
          onClose={() => setPreviewTrader(null)}
        />
      )}
    </div>
  );
};

export default Traders;
